import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";

import { MapPin, Calendar, DollarSign, Copy } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { STATUS_COLORS, STATUS_OPTIONS } from "@shared/utils/constants";
import { format } from "date-fns";

const PackageCard = ({ pkg }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customer, status, totalAmount, createdAt, trackingNumber } = pkg;

  const copyTrackingNumber = async () => {
    if (trackingNumber) {
      try {
        await navigator.clipboard.writeText(
          trackingNumber.replace("MA", "SP-")
        );
        toast.success(t("packages.trackingNumberCopied"));
      } catch {
        toast.error(t("packages.copyError"));
      }
    }
  };

  return (
    <Card
      className="group hover:shadow-sm hover:shadow-primary/5 transition-all duration-200 border-l-2 border-l-muted hover:border-l-primary/60 relative overflow-hidden cursor-pointer"
      onClick={() => navigate(`/packages/${pkg._id}/view`)}
    >
      <CardContent className="p-3 relative">
        {/* Compact header row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge
              className={`${STATUS_COLORS[status]} text-xs px-2 py-0.5`}
              variant="outline"
              size="sm"
            >
              {t(STATUS_OPTIONS[status])}
            </Badge>
          </div>

          <div className="flex items-center gap-1 text-right">
            <DollarSign className="h-3.5 w-3.5 text-green-600" />
            <span className="font-semibold text-sm">
              {totalAmount.toFixed(2)}
            </span>
            <span className="text-xs text-muted-foreground">MAD</span>
          </div>
        </div>

        {/* Customer info row */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex gap-2 flex-1 min-w-0">
            <h4 className="font-medium text-sm text-foreground truncate">
              {customer.name}
            </h4>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{customer.city}</span>
            </div>
          </div>

          <div className="flex items-center gap-1 text-xs text-muted-foreground ml-2">
            <Calendar className="h-3 w-3" />
            <span>{format(new Date(createdAt), "dd/MM/yyyy")}</span>
          </div>
        </div>

        {/* Compact tracking number */}
        {trackingNumber && (
          <div
            className="bg-muted/30 rounded px-2 py-1 text-xs border border-dashed border-muted-foreground/20 relative z-10 flex items-center justify-between"
            onClick={(e) => {
              e.stopPropagation();
              copyTrackingNumber();
            }}
          >
            <code className="font-mono font-medium text-foreground">
              {trackingNumber.replace("MA", "SP-")}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyTrackingNumber();
              }}
              className="text-xs text-primary hover:text-primary/80 transition-colors cursor-pointer ml-2 flex items-center gap-1"
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PackageCard;
